import { supabase } from "@/integrations/supabase/client";
import { ONBOARDING_STEPS, BASIC_ONBOARDING_STEPS, EVOLUTION_ONBOARDING_STEPS, type OnboardingStep } from "@/constants/onboarding";

export interface OnboardingStepRecord {
  id: string;
  empresa_id: string;
  step: OnboardingStep;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingProgress {
  isLegacyUser?: boolean;
  isNewUser?: boolean;
  completed: number;
  total: number;
  percentage: number;
  steps?: OnboardingStepRecord[];
  shouldShowChecklist: boolean;
  shouldShowEvolvementCard: boolean;
}

/**
 * Obter todas as etapas de onboarding de uma empresa
 */
export async function getOnboardingSteps(empresaId: string): Promise<OnboardingStepRecord[]> {
  const { data, error } = await supabase
    .from("empresa_onboarding")
    .select("*")
    .eq("empresa_id", empresaId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Erro ao buscar etapas de onboarding:", error);
    return [];
  }

  return data || [];
}

/**
 * Marcar uma etapa como concluída
 */
export async function completeOnboardingStep(
  empresaId: string,
  step: OnboardingStep
): Promise<boolean> {
  console.log(`📝 Tentando marcar etapa como concluída:`, { empresaId, step });

  const { data, error } = await supabase
    .from("empresa_onboarding")
    .upsert(
      {
        empresa_id: empresaId,
        step,
        completed: true,
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "empresa_id,step" }
    )
    .select();

  if (error) {
    console.error(`❌ Erro ao completar etapa ${step}:`, error);
    return false;
  }

  console.log(`✅ Etapa ${step} concluída com sucesso!`, data);
  return true;
}

/**
 * Verificar se uma etapa foi completada
 */
export async function isStepCompleted(
  empresaId: string,
  step: OnboardingStep
): Promise<boolean> {
  const { data, error } = await supabase
    .from("empresa_onboarding")
    .select("completed")
    .eq("empresa_id", empresaId)
    .eq("step", step)
    .single();

  if (error) {
    return false;
  }

  return data?.completed ?? false;
}

/**
 * Obter progresso do onboarding com compatibilidade para usuários legados
 *
 * Estratégia:
 * 1. Se tem dados na tabela nova → usar dados novos
 * 2. Se não tem mas onboarding_completed=true em empresas → usuário legado (já fez v1)
 * 3. Se não tem nada → novo usuário
 */
export async function getOnboardingProgress(empresaId: string): Promise<OnboardingProgress> {
  // Buscar dados da nova tabela
  const newSteps = await getOnboardingSteps(empresaId);

  // Se tem dados novos, usar o novo fluxo
  if (newSteps.length > 0) {
    // Buscar flag de evolução completa
    const { data: empresa } = await supabase
      .from("empresas")
      .select("onboarding_evolution_completed")
      .eq("id", empresaId)
      .single();

    const basicSteps = newSteps.filter(s => BASIC_ONBOARDING_STEPS.includes(s.step as OnboardingStep));
    const completed = basicSteps.filter(s => s.completed).length;
    const total = BASIC_ONBOARDING_STEPS.length;

    // Se já finalizou a evolução, não mostrar mais o card
    const isEvolutionComplete = empresa?.onboarding_evolution_completed ?? false;

    return {
      completed,
      total,
      percentage: (completed / total) * 100,
      steps: newSteps,
      shouldShowChecklist: completed < total,
      shouldShowEvolvementCard: completed === total && !isEvolutionComplete,
    };
  }

  // Se não tem dados novos, verificar se é usuário LEGADO (v1)
  const { data: empresa, error } = await supabase
    .from("empresas")
    .select("onboarding_completed, onboarding_completed_at")
    .eq("id", empresaId)
    .single();

  if (error) {
    console.error("Erro ao buscar dados da empresa:", error);
    return {
      completed: 0,
      total: BASIC_ONBOARDING_STEPS.length,
      percentage: 0,
      shouldShowChecklist: true,
      shouldShowEvolvementCard: false,
    };
  }

  // Usuário antigo que já completou o onboarding v1
  if (empresa?.onboarding_completed) {
    return {
      isLegacyUser: true,
      completed: BASIC_ONBOARDING_STEPS.length,
      total: BASIC_ONBOARDING_STEPS.length,
      percentage: 100,
      shouldShowChecklist: false,
      shouldShowEvolvementCard: true,
    };
  }

  // Novo usuário, nada completado
  return {
    isNewUser: true,
    completed: 0,
    total: BASIC_ONBOARDING_STEPS.length,
    percentage: 0,
    shouldShowChecklist: true,
    shouldShowEvolvementCard: false,
  };
}

/**
 * Obter progresso do "Evolua seu Ateliê" (etapas avançadas)
 */
export async function getEvolvementProgress(empresaId: string) {
  const steps = await getOnboardingSteps(empresaId);

  const evolutionSteps = [
    ONBOARDING_STEPS.FIRST_PRODUCTS,
    ONBOARDING_STEPS.FIRST_SERVICES,
    ONBOARDING_STEPS.CREATE_ORDER,
    ONBOARDING_STEPS.FIRST_NF,
    ONBOARDING_STEPS.FIRST_TEAM_MEMBER,
  ];

  const completed = steps.filter(s => evolutionSteps.includes(s.step as any) && s.completed).length;

  return {
    completed,
    total: evolutionSteps.length,
    percentage: (completed / evolutionSteps.length) * 100,
    steps: steps.filter(s => evolutionSteps.includes(s.step as any)),
  };
}
